import { c as _c } from "react/compiler-runtime";
// Seller onboarding pipeline.
//
// TABLE FIRST. The founder rejected the kanban as the default view — «таблицу
// надо делать, ребят. Это очень сложно вот так обрабатывать визуально» — asking
// instead for date / status / manager columns with filters and stalled rows
// burning red. The board survives behind a view toggle because dragging a card
// through stages is still the better tool for the manager doing the work.
//
// Attention alerts are derived from the rows themselves (unowned / stuck too
// long) — nothing is scheduled or persisted.
//
// Drag-and-drop uses the native HTML5 API on purpose — no dependency, and the
// board is small enough that pointer-sensor niceties aren't worth a library.
// Keyboard users get the same moves from the stage select in the card.
//
// CRM (04.10.2026): строка и карточка доски открывают полную карточку заявки
// (/applications/:id) вместо модалки; над таблицей — «Мои задачи», в таблице —
// следующий шаг, источник и теги, плюс заведение заявки руками, загрузка базы
// из таблицы и выгрузка. Поля CRM в строке доски необязательные: без них
// раздел выглядит и работает как раньше.

import { CRM_LEAD_SOURCE_LABELS, CRM_LEAD_SOURCES, type CrmLeadSource } from "@osmix/contracts";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { AlertTriangle, CalendarClock, Download, GripVertical, LayoutGrid, Plus, RefreshCw, Table2, Upload, UserX } from "lucide-react";
import { type DragEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { APPLICATION_STATUSES, type ApplicationStatus, type SellerApplication } from "@/api";
import { useExportLeads } from "@/api/hooks/use-application-crm";
import { useApplicationBoard, useMoveApplication } from "@/api/hooks/use-applications";
import { AdminDetailModal } from "@/components/admin-detail-modal";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CLOSED_STATUSES, errorText, formatDay, formatDue, overdueBy } from "@/components/crm/format";
import { ImportLeadsDialog } from "@/components/crm/import-leads-dialog";
import { MyTasksPanel } from "@/components/crm/my-tasks-panel";
import { NewLeadDialog } from "@/components/crm/new-lead-dialog";
import { TASK_ICON } from "@/components/crm/tasks-panel";
import { downloadBlob, PRIORITY_VARIANT, STATUS_ACCENT, Textarea } from "@/components/crm/ui";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { Segmented } from "@/components/segmented";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useDataTable } from "@/lib/use-data-table";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";

// Цвет стадии (STATUS_ACCENT) и вид бейджа приоритета (PRIORITY_VARIANT) — в
// components/crm/ui: их же показывает карточка заявки.

// --- Priority and alerts ---------------------------------------------------
// Priority is DERIVED, never typed in (правка 7): «система сама выставляет
// высокий приоритет, если увидит, что 4 дня без ответа». С 26.09.2026 сроки,
// приоритет и предупреждения считает шлюз (`application.sla`, дни —
// календарные по Москве): раньше их считал браузер по своим часам, и у двух
// менеджеров одна заявка могла быть и «просрочена», и «в срок». Здесь — только
// отображение.

type AlertSeverity = "error" | "warning";
type BoardAlert = {
  id: string;
  applicationId: string;
  severity: AlertSeverity;
  kind: "stale" | "unassigned";
  shopName: string;
  /** Owner to blame, or null when that IS the problem. */
  assigneeName: string | null;
  days: number;
};

/** Плоский список предупреждений доски из того, что прислал сервер. */
const collectAlerts = (applications: SellerApplication[]): BoardAlert[] => applications.flatMap(application => application.sla.alerts.map(alert => ({
  id: `${application.id}:${alert.kind}`,
  applicationId: application.id,
  severity: alert.severity,
  kind: alert.kind,
  shopName: application.shopName,
  assigneeName: alert.kind === "unassigned" ? null : application.assignee?.name ?? null,
  days: application.sla.daysInStage
})))
// Worst first, then longest-waiting: the top three are the ones that matter.
.sort((a, b) => {
  if (a.severity !== b.severity) return a.severity === "error" ? -1 : 1;
  return b.days - a.days;
});

/** How many badges stay on the board before collapsing into "+N more". */
const VISIBLE_ALERTS = 3;
const ALERT_CLASS: Record<AlertSeverity, string> = {
  error: "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-500"
};
function AlertBadge(t0) {
  const $ = _c(21);
  const {
    alert,
    onSelect
  } = t0;
  const {
    t
  } = useTranslation();
  const Icon = alert.kind === "unassigned" ? UserX : AlertTriangle;
  let t1;
  if ($[0] !== alert.days || $[1] !== alert.kind || $[2] !== t) {
    t1 = alert.kind === "unassigned" ? t("applications.alert.unassigned") : t("applications.alert.stale", {
      count: alert.days
    });
    $[0] = alert.days;
    $[1] = alert.kind;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const label = t1;
  const t2 = ALERT_CLASS[alert.severity];
  let t3;
  if ($[4] !== t2) {
    t3 = cn("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== Icon) {
    t4 = <Icon className="size-3.5 shrink-0" />;
    $[6] = Icon;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== alert.shopName) {
    t5 = <span className="font-semibold">{alert.shopName}</span>;
    $[8] = alert.shopName;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== label) {
    t6 = <span className="opacity-80">— {label}</span>;
    $[10] = label;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== alert.assigneeName) {
    t7 = alert.assigneeName && <span className="opacity-70">· {alert.assigneeName}</span>;
    $[12] = alert.assigneeName;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== onSelect || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <button type="button" onClick={onSelect} className={t3}>{t4}{t5}{t6}{t7}</button>;
    $[14] = onSelect;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function Avatar(t0) {
  const $ = _c(3);
  const {
    initials,
    title
  } = t0;
  let t1;
  if ($[0] !== initials || $[1] !== title) {
    t1 = <span title={title} className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">{initials}</span>;
    $[0] = initials;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Следующий шаг строкой: что, когда, просрочен ли. `undefined` — шлюз полей
 * CRM не прислал (ничего не показываем), `null` — шага нет.
 */
function NextStepLine(t0) {
  const $ = _c(27);
  const {
    application,
    className
  } = t0;
  const next = application.nextTask;
  if (next === undefined) {
    return null;
  }
  if (next === null) {
    if (CLOSED_STATUSES.includes(application.status)) {
      return null;
    }
    let t1;
    if ($[0] !== className) {
      t1 = cn("flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-500", className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CalendarClock className="size-3.5 shrink-0" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t1) {
      t3 = <p className={t1}>{t2}Нет следующего шага</p>;
      $[3] = t1;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  const Icon = TASK_ICON[next.kind];
  const t1 = next.overdue ? "text-destructive" : "text-muted-foreground";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("flex min-w-0 items-start gap-1.5 text-xs", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== Icon) {
    t3 = <Icon className="mt-0.5 size-3.5 shrink-0" />;
    $[8] = Icon;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = next.overdue ? "font-medium" : "text-foreground";
  let t5;
  if ($[10] !== t4) {
    t5 = cn("block truncate", t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== next.title || $[13] !== t5) {
    t6 = <span className={t5}>{next.title}</span>;
    $[12] = next.title;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== next.dueAt || $[16] !== next.overdue) {
    t7 = next.overdue ? overdueBy(next.dueAt) : formatDue(next.dueAt);
    $[15] = next.dueAt;
    $[16] = next.overdue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = <span className="block">{t7}</span>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <span className="min-w-0">{t6}{t8}</span>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t2 || $[24] !== t3 || $[25] !== t9) {
    t10 = <p data-testid="application-next-step" className={t2}>{t3}{t9}</p>;
    $[23] = t2;
    $[24] = t3;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function ApplicationCard(t0) {
  const $ = _c(48);
  const {
    application,
    onOpen,
    onDragStart,
    dragging
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    daysInStage: stale,
    priority,
    severity
  } = application.sla;
  const t1 = dragging && "opacity-40";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("group relative cursor-pointer rounded-lg border bg-card p-3 shadow-xs transition-shadow hover:shadow-md", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GripVertical className="mt-0.5 size-4 shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== application.shopName) {
    t4 = <p className="truncate text-sm font-medium">{application.shopName}</p>;
    $[3] = application.shopName;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== application.city || $[6] !== application.contactName) {
    t5 = <p className="truncate text-xs text-muted-foreground">{application.contactName} · {application.city}</p>;
    $[5] = application.city;
    $[6] = application.contactName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== application.categories) {
    t6 = application.categories.map(_temp);
    $[8] = application.categories;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== application.tags) {
    t7 = application.tags?.map(_temp2);
    $[10] = application.tags;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="mt-2 flex flex-wrap gap-1">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== application) {
    t9 = <NextStepLine application={application} className="mt-2" />;
    $[15] = application;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== application.assignee || $[18] !== t) {
    t10 = application.assignee ? <Avatar initials={application.assignee.initials} title={application.assignee.name} /> : <span title={t("applications.unassigned")} className="flex size-6 shrink-0 items-center justify-center rounded-full border border-dashed text-muted-foreground"><Plus className="size-3" /></span>;
    $[17] = application.assignee;
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== priority || $[21] !== t) {
    t11 = priority !== "low" && <Badge variant={PRIORITY_VARIANT[priority]} className="text-[10px]">{t(`applications.priority.${priority}`)}</Badge>;
    $[20] = priority;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = severity === "error" && "font-medium text-destructive";
  const t13 = severity === "warning" && "font-medium text-amber-600 dark:text-amber-500";
  const t14 = !severity && "text-muted-foreground";
  let t15;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t14) {
    t15 = cn("ml-auto text-[11px] tabular-nums", t12, t13, t14);
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== stale || $[28] !== t) {
    t16 = t("applications.daysInStage", {
      count: stale
    });
    $[27] = stale;
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15 || $[31] !== t16) {
    t17 = <span data-testid="application-card-days" className={t15}>{t16}</span>;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t17) {
    t18 = <div className="mt-3 flex items-center gap-2">{t10}{t11}{t17}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18 || $[38] !== t4 || $[39] !== t5 || $[40] !== t8 || $[41] !== t9) {
    t19 = <div className="flex items-start gap-2">{t3}<div className="min-w-0 flex-1">{t4}{t5}{t8}{t9}{t18}</div></div>;
    $[37] = t18;
    $[38] = t4;
    $[39] = t5;
    $[40] = t8;
    $[41] = t9;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== onDragStart || $[44] !== onOpen || $[45] !== t19 || $[46] !== t2) {
    t20 = <div draggable={true} onDragStart={onDragStart} onClick={onOpen} className={t2}>{t19}</div>;
    $[43] = onDragStart;
    $[44] = onOpen;
    $[45] = t19;
    $[46] = t2;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  return t20;
}
function _temp2(tag) {
  return <Badge key={`tag:${tag}`} variant="secondary" className="text-[10px] font-normal">#{tag}</Badge>;
}
function _temp(category) {
  return <Badge key={category} variant="outline" className="text-[10px] font-normal">{category}</Badge>;
}
type ApplicationView = "table" | "board";
type SortKey = "shop" | "submitted" | "status" | "assignee" | "next" | "days" | "priority" | "source";
export function ApplicationsPage() {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth(s => s.user);
  const canManage = hasAdminPermission(user, "action.applications.manage");
  const canExport = hasAdminPermission(user, "action.exports.run");
  const {
    data,
    error,
    isFetching,
    refetch
  } = useApplicationBoard();
  const move = useMoveApplication();
  const exportLeads = useExportLeads();
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const openApplication = (id: string) => navigate(`/applications/${id}`);
  const runExport = () => {
    setExportError(null);
    exportLeads.mutate(undefined, {
      onSuccess: file => downloadBlob(file.blob, file.filename),
      onError: e => setExportError(errorText(e))
    });
  };
  // «Отклонён» — решение с причиной (продавец её увидит, дальше блок 90 дней),
  // поэтому и из доски, и из карточки сначала спрашиваем причину.
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const requestMove = (id_0: string, status: ApplicationStatus) => {
    if (status === "rejected") {
      setRejecting(id_0);
      setRejectReason("");
      return;
    }
    move.mutate({
      id: id_0,
      status
    });
  };

  // The dashboard chips deep-link in with ?status=… / ?alerts=1, so the filter
  // state lives in the URL and a chip click lands pre-filtered.
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status") ?? "";
  const assigneeFilter = params.get("assignee") ?? "";
  const onlyAlerts = params.get("alerts") === "1";
  const search = params.get("q") ?? "";
  // Period filter. The table had none at all — «здесь тоже нету периодов»;
  // Айталы: «как фильтрация, просто добавьте, и всё». Both bounds are optional
  // so either can be used on its own.
  const submittedFrom = params.get("from") ?? "";
  const submittedTo = params.get("to") ?? "";
  // Фильтры CRM: источник, тег, «без следующего шага», «шаг просрочен».
  const sourceFilter = params.get("source") ?? "";
  const tagFilter = params.get("tag") ?? "";
  const noStep = params.get("nostep") === "1";
  const stepOverdue = params.get("stepOverdue") === "1";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const [view, setView] = useState<ApplicationView>("table");
  const [alertsOpen, setAlertsOpen] = useState(false);
  const applications = data?.applications ?? [];
  const alerts = useMemo(() => collectAlerts(applications), [applications]);
  // Шлюз без CRM полей не присылает — тогда новые колонки и фильтры не показываем.
  const hasCrm = applications.some(entry => entry.source !== undefined || entry.nextTask !== undefined);
  const sources = useMemo(() => CRM_LEAD_SOURCES.filter(source => applications.some(entry_0 => entry_0.source === source)), [applications]);
  const tags = useMemo(() => [...new Set(applications.flatMap(entry_1 => entry_1.tags ?? []))].sort((a, b) => a.localeCompare(b, "ru")), [applications]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return applications.filter(application => {
      if (statusFilter && application.status !== statusFilter) return false;
      if (assigneeFilter) {
        const id_1 = application.assignee?.id ?? "";
        // "unassigned" is a filter value in its own right — it's the case she
        // most wants to find.
        if (assigneeFilter === "none" ? id_1 !== "" : id_1 !== assigneeFilter) return false;
      }
      if (onlyAlerts && !application.sla.severity) return false;
      if (sourceFilter && application.source !== sourceFilter) return false;
      if (tagFilter && !application.tags?.includes(tagFilter)) return false;
      // «Без шага» — только на открытых стадиях и только когда шлюз прямо сказал «шага нет» (null).
      if (noStep && (application.nextTask !== null || CLOSED_STATUSES.includes(application.status))) return false;
      if (stepOverdue && !application.nextTask?.overdue) return false;
      // Compare on the ISO day: submittedAt is a timestamp, the filter bounds
      // are calendar days and `to` must include the whole day it names.
      const submittedDay = application.submittedAt.slice(0, 10);
      if (submittedFrom && submittedDay < submittedFrom) return false;
      if (submittedTo && submittedDay > submittedTo) return false;
      if (needle) {
        const haystack = `${application.shopName} ${application.contactName} ${application.city} ${application.phone ?? ""} ${(application.tags ?? []).join(" ")}`;
        if (!haystack.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [applications, statusFilter, assigneeFilter, onlyAlerts, search, submittedFrom, submittedTo, sourceFilter, tagFilter, noStep, stepOverdue]);
  const table = useDataTable<SellerApplication, SortKey>({
    rows: filtered,
    pageSize: 25,
    // Longest-waiting first: the table opens on the problems.
    initialSort: {
      key: "days",
      dir: "desc"
    },
    accessors: {
      shop: a_0 => a_0.shopName,
      submitted: a_1 => a_1.submittedAt,
      status: a_2 => APPLICATION_STATUSES.indexOf(a_2.status),
      assignee: a_3 => a_3.assignee?.name ?? "",
      // Без срока и без шага — в конец (пустое значение сортировщик ставит последним).
      next: a_4 => a_4.nextTask?.dueAt ? Date.parse(a_4.nextTask.dueAt) : null,
      days: a_5 => a_5.sla.daysInStage,
      priority: a_6 => ({
        critical: 0,
        high: 1,
        normal: 2,
        low: 3
      })[a_6.sla.priority],
      source: a_7 => a_7.source ? CRM_LEAD_SOURCE_LABELS[a_7.source] : ""
    }
  });
  if (error) return <p className="text-destructive">{error.message}</p>;
  if (!data) return <p className="text-muted-foreground">{t("common.loading")}</p>;
  const danger = alerts.filter(alert => alert.severity === "error").length;
  const warning = alerts.length - danger;
  return <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 data-tour="applications-intro" className="text-2xl font-bold tracking-tight">{t("applications.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("applications.description")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && <Button onClick={() => setNewLeadOpen(true)}>
              <Plus className="size-4" />
              Новая заявка
            </Button>}
          {canManage && <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="size-4" />
              Загрузить базу
            </Button>}
          {canExport && <Button variant="outline" disabled={exportLeads.isPending} onClick={runExport} title="Все заявки с полями CRM в xlsx">
              <Download className="size-4" />
              Выгрузить
            </Button>}
          {/* Ручное обновление: заявки с лендинга приходят в реальном времени, а
           авто-рефетч раз в 30 с иногда «прямо сейчас» не устраивает —
           кнопка подтягивает список немедленно. */}
          <button type="button" onClick={() => void refetch()} disabled={isFetching} className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60" title="Обновить список заявок">
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            Обновить
          </button>
          <div data-tour="applications-view">
          <Segmented value={view} onChange={setView} options={[{
            value: "table",
            label: t("applications.view.table"),
            icon: Table2
          }, {
            value: "board",
            label: t("applications.view.board"),
            icon: LayoutGrid
          }]} />
          </div>
        </div>
      </div>

      {exportError && <p className="text-sm text-destructive">Выгрузка не удалась: {exportError}</p>}

      {/* Работа на сегодня — до таблицы: просроченное видно первым. */}
      <MyTasksPanel />

      {/* «Сноски»: per-status counters that double as filters. */}
      <div data-tour="applications-funnel" className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setParam("status", "")} className={cn("flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors hover:bg-muted/50", !statusFilter && "border-primary bg-primary/10")}>
          <span className="text-muted-foreground">{t("applications.filter.all")}</span>
          <span className="font-semibold tabular-nums">{applications.length}</span>
        </button>

        {APPLICATION_STATUSES.map(status_0 => {
        const count = applications.filter(entry_2 => entry_2.status === status_0).length;
        return <button key={status_0} type="button" onClick={() => setParam("status", statusFilter === status_0 ? "" : status_0)} className={cn("flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors hover:bg-muted/50", statusFilter === status_0 && "border-primary bg-primary/10")}>
              <span className={cn("size-2 rounded-full", STATUS_ACCENT[status_0])} />
              <span className="text-muted-foreground">{t(`applications.status.${status_0}`)}</span>
              <span className="font-semibold tabular-nums">{count}</span>
            </button>;
      })}

        {alerts.length > 0 && <button type="button" data-tour="applications-alerts" onClick={() => setParam("alerts", onlyAlerts ? "" : "1")} className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors", danger > 0 ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20" : "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-500", onlyAlerts && "ring-2 ring-primary/40")}>
            <AlertTriangle className="size-3.5" />
            {t("applications.filter.problems", {
          count: danger + warning
        })}
          </button>}

        {alerts.length > 0 && <Button variant="ghost" size="sm" onClick={() => setAlertsOpen(true)}>
            {t("applications.alert.byManager")}
          </Button>}
      </div>

      {view === "table" ? <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={search} placeholder={t("applications.filter.search")} className="h-9 w-56" onChange={e_0 => setParam("q", e_0.target.value)} />
            <Select value={statusFilter} onChange={e_1 => setParam("status", e_1.target.value)}>
              <option value="">{t("applications.filter.anyStatus")}</option>
              {APPLICATION_STATUSES.map(status_1 => <option key={status_1} value={status_1}>
                  {t(`applications.status.${status_1}`)}
                </option>)}
            </Select>
            <Select value={assigneeFilter} onChange={e_2 => setParam("assignee", e_2.target.value)}>
              <option value="">{t("applications.filter.anyAssignee")}</option>
              <option value="none">{t("applications.unassigned")}</option>
              {data.assignees.map(assignee => <option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </option>)}
            </Select>

            {/* Period bounds sit with the other filters, not behind a popover:
             on a working table they are used as often as the status. */}
            <div className="flex items-center gap-1">
              <Input type="date" aria-label={t("applications.filter.from")} title={t("applications.filter.from")} className="h-9 w-[9.5rem]" value={submittedFrom} max={submittedTo || undefined} onChange={e_3 => setParam("from", e_3.target.value)} />
              <span className="text-xs text-muted-foreground">—</span>
              <Input type="date" aria-label={t("applications.filter.to")} title={t("applications.filter.to")} className="h-9 w-[9.5rem]" value={submittedTo} min={submittedFrom || undefined} onChange={e_4 => setParam("to", e_4.target.value)} />
              {(submittedFrom || submittedTo) && <Button size="sm" variant="ghost" onClick={() => {
            const next_0 = new URLSearchParams(params);
            next_0.delete("from");
            next_0.delete("to");
            setParams(next_0, {
              replace: true
            });
          }}>
                  {t("applications.filter.resetPeriod")}
                </Button>}
            </div>

            {hasCrm && <>
                <Select aria-label="Источник" value={sourceFilter} onChange={e_5 => setParam("source", e_5.target.value)}>
                  <option value="">Любой источник</option>
                  {sources.map(source_0 => <option key={source_0} value={source_0}>
                      {CRM_LEAD_SOURCE_LABELS[source_0 as CrmLeadSource]}
                    </option>)}
                </Select>
                {tags.length > 0 && <Select aria-label="Тег" value={tagFilter} onChange={e_6 => setParam("tag", e_6.target.value)}>
                    <option value="">Любой тег</option>
                    {tags.map(tag => <option key={tag} value={tag}>
                        {tag}
                      </option>)}
                  </Select>}
                <button type="button" aria-pressed={noStep} onClick={() => setParam("nostep", noStep ? "" : "1")} className={cn("inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors hover:bg-muted/50", noStep && "border-primary bg-primary/10")}>
                  <CalendarClock className="size-4 text-muted-foreground" />
                  Без следующего шага
                </button>
                <button type="button" aria-pressed={stepOverdue} onClick={() => setParam("stepOverdue", stepOverdue ? "" : "1")} className={cn("inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors hover:bg-muted/50", stepOverdue && "border-destructive/50 bg-destructive/10 text-destructive")}>
                  <AlertTriangle className="size-4" />
                  Шаг просрочен
                </button>
              </>}
          </div>

          {filtered.length === 0 ? <p className="text-muted-foreground">{t("applications.empty")}</p> : <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead label={t("applications.col.shop")} sortKey="shop" sort={table.sort} onToggle={table.toggleSort} />
                    <SortableHead label={t("applications.submitted")} sortKey="submitted" sort={table.sort} onToggle={table.toggleSort} />
                    <SortableHead label={t("applications.col.status")} sortKey="status" sort={table.sort} onToggle={table.toggleSort} />
                    <SortableHead label={t("applications.col.assignee")} sortKey="assignee" sort={table.sort} onToggle={table.toggleSort} />
                    {hasCrm && <SortableHead label="Следующий шаг" sortKey="next" sort={table.sort} onToggle={table.toggleSort} />}
                    <SortableHead label={t("applications.col.days")} sortKey="days" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />
                    <SortableHead label={t("applications.col.priority")} sortKey="priority" sort={table.sort} onToggle={table.toggleSort} />
                    {hasCrm && <SortableHead label="Источник" sortKey="source" sort={table.sort} onToggle={table.toggleSort} />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.pageRows.map(application_0 => {
              const {
                severity,
                daysInStage: days,
                priority,
                slaDays,
                daysLeft,
                overdue
              } = application_0.sla;
              return <TableRow key={application_0.id} className={cn("cursor-pointer",
              // «Они у меня выделены, красным горят» — the whole row,
              // not a subtle icon.
              severity === "error" && "bg-destructive/10 hover:bg-destructive/15", severity === "warning" && "bg-amber-500/10 hover:bg-amber-500/15")} data-application-id={application_0.id} data-severity={severity ?? ""} onClick={() => openApplication(application_0.id)}>
                        <TableCell className="max-w-64 min-w-40 whitespace-normal">
                          <span className="font-medium">{application_0.shopName}</span>
                          <span className="block text-xs text-muted-foreground">
                            {application_0.contactName} · {application_0.city}
                          </span>
                          {/* Теги — под названием: отдельной колонкой таблица на 1440 уезжала вбок. */}
                          {application_0.tags && application_0.tags.length > 0 && <TagList tags={application_0.tags} className="mt-1" />}
                        </TableCell>
                        <TableCell className="text-muted-foreground tabular-nums">
                          {formatDay(application_0.submittedAt)}
                        </TableCell>
                        {/* Статус и менеджер переносятся: с колонкой «Следующий шаг» таблица иначе не помещается в 1280. */}
                        <TableCell className="min-w-28 whitespace-normal">
                          <span className="flex items-center gap-2">
                            <span className={cn("size-2 shrink-0 rounded-full", STATUS_ACCENT[application_0.status])} />
                            {t(`applications.status.${application_0.status}`)}
                          </span>
                        </TableCell>
                        <TableCell className="min-w-28 whitespace-normal">
                          {application_0.assignee ? application_0.assignee.name : <span className="inline-flex items-center gap-1.5 text-destructive">
                              <UserX className="size-3.5 shrink-0" />
                              {t("applications.unassigned")}
                            </span>}
                        </TableCell>
                        {hasCrm && <TableCell className="max-w-40 min-w-32 whitespace-normal">
                            <NextStepLine application={application_0} />
                            {(application_0.nextTask === undefined || application_0.nextTask === null && CLOSED_STATUSES.includes(application_0.status)) && <span className="text-muted-foreground">—</span>}
                          </TableCell>}
                        <TableCell className={cn("text-right font-semibold tabular-nums", severity === "error" && "text-destructive", severity === "warning" && "text-amber-600 dark:text-amber-500")}>
                          {t("applications.daysInStage", {
                    count: days
                  })}
                          {daysLeft !== null && <span data-testid="application-sla" className={cn("block text-xs font-normal", overdue ? "text-destructive" : "text-muted-foreground")}>
                              {overdue ? t("applications.sla.overdue") : t("applications.sla.daysLeft", {
                      count: daysLeft
                    })}
                            </span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={PRIORITY_VARIANT[priority]} data-testid="application-priority"
                  // The badge has to explain itself: nobody set this,
                  // the SLA did.
                  title={t("applications.priorityAuto", {
                    sla: slaDays
                  })}>
                            {t(`applications.priority.${priority}`)}
                          </Badge>
                        </TableCell>
                        {hasCrm && <TableCell className="max-w-32 min-w-24 text-xs break-words whitespace-normal text-muted-foreground">
                            {application_0.source ? CRM_LEAD_SOURCE_LABELS[application_0.source] : "—"}
                          </TableCell>}
                      </TableRow>;
            })}
                </TableBody>
              </Table>
              <DataTablePagination table={table} />
            </>}
        </div> : <ApplicationsBoard applications={filtered} onOpen={openApplication} onMove={requestMove} />}

      <ConfirmDialog open={rejecting !== null} title="Отклонить заявку?" description="Продавец получит уведомление с причиной. Повторно подать заявку он сможет только через 90 дней." confirmLabel="Отклонить" confirmVariant="destructive" busy={move.isPending} onClose={() => setRejecting(null)} onConfirm={() => {
      if (!rejecting || !rejectReason.trim()) return;
      move.mutate({
        id: rejecting,
        status: "rejected",
        reason: rejectReason.trim()
      }, {
        onSettled: () => setRejecting(null)
      });
    }}>
        <Textarea value={rejectReason} aria-label="Причина отказа" onChange={e_7 => setRejectReason(e_7.target.value)} placeholder="Причина отказа — обязательно" className="min-h-24" />
      </ConfirmDialog>

      {alertsOpen && <AlertsModal alerts={alerts} onSelect={applicationId => {
      setAlertsOpen(false);
      openApplication(applicationId);
    }} onClose={() => setAlertsOpen(false)} />}

      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} assignees={data.assignees} />
      <ImportLeadsDialog open={importOpen} onOpenChange={setImportOpen} assignees={data.assignees} />
    </div>;
}

/** Теги строки: два видны, остальные — «+N» с подсказкой. */
function TagList(t0) {
  const $ = _c(15);
  const {
    tags,
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== tags) {
    const rest = tags.slice(2);
    if ($[6] !== className) {
      t1 = cn("flex flex-wrap gap-1", className);
      $[6] = className;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    t2 = "application-tags";
    if ($[8] !== tags) {
      t3 = tags.slice(0, 2).map(_temp18);
      $[8] = tags;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t4 = rest.length > 0 && <Badge variant="outline" className="text-[10px] font-normal" title={rest.join(", ")}>+{rest.length}</Badge>;
    $[0] = className;
    $[1] = tags;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <span className={t1} data-testid={t2}>{t3}{t4}</span>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

/**
 * The kanban, now a secondary view. Presentational: the page owns data and
 * mutations, so table and board always agree on what is filtered.
 */
function _temp18(tag) {
  return <Badge key={tag} variant="secondary" className="text-[10px] font-normal">{tag}</Badge>;
}
function ApplicationsBoard(t0) {
  const $ = _c(12);
  const {
    applications,
    onOpen,
    onMove
  } = t0;
  const {
    t
  } = useTranslation();
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  let t1;
  if ($[0] !== draggingId || $[1] !== onMove) {
    t1 = status => {
      setDropTarget(null);
      const id = draggingId;
      setDraggingId(null);
      if (id) {
        onMove(id, status);
      }
    };
    $[0] = draggingId;
    $[1] = onMove;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onDrop = t1;
  let t2;
  if ($[3] !== applications || $[4] !== draggingId || $[5] !== dropTarget || $[6] !== onDrop || $[7] !== onOpen || $[8] !== t) {
    t2 = APPLICATION_STATUSES.map(status_0 => {
      const cards = applications.filter(entry => entry.status === status_0);
      return <div key={status_0} onDragOver={event => {
        event.preventDefault();
        setDropTarget(status_0);
      }} onDragLeave={() => setDropTarget(current => current === status_0 ? null : current)} onDrop={() => onDrop(status_0)} className={cn("flex w-72 shrink-0 flex-col gap-3 rounded-xl border bg-muted/30 p-3 transition-colors", dropTarget === status_0 && "border-primary bg-primary/5")}><div className="flex items-center gap-2"><span className={cn("size-2 rounded-full", STATUS_ACCENT[status_0])} /><p className="text-sm font-semibold">{t(`applications.status.${status_0}`)}</p><span className="ml-auto text-xs tabular-nums text-muted-foreground">{cards.length}</span></div><div className="flex flex-col gap-2">{cards.map(application => <ApplicationCard key={application.id} application={application} dragging={draggingId === application.id} onDragStart={() => setDraggingId(application.id)} onOpen={() => onOpen(application.id)} />)}{cards.length === 0 && <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">{t("applications.empty")}</p>}</div></div>;
    });
    $[3] = applications;
    $[4] = draggingId;
    $[5] = dropTarget;
    $[6] = onDrop;
    $[7] = onOpen;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <div className="flex flex-col gap-3"><div className="flex gap-4 overflow-x-auto pb-4">{t2}</div></div>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

/**
 * Every alert, grouped by the manager responsible — the view that answers
 * "who is missing things". Unowned cards come first, since nobody is watching
 * them at all. Selecting a row jumps to that card.
 */
function AlertsModal(t0) {
  const $ = _c(27);
  const {
    alerts,
    onSelect,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== alerts || $[1] !== onClose || $[2] !== onSelect || $[3] !== t) {
    const groups = new Map();
    for (const alert of alerts) {
      const key = alert.assigneeName ?? "";
      groups.set(key, [...(groups.get(key) ?? []), alert]);
    }
    const ordered = [...groups.entries()].sort(_temp19);
    T0 = AdminDetailModal;
    t3 = true;
    if ($[10] !== alerts.length || $[11] !== t) {
      t4 = t("applications.alert.title", {
        count: alerts.length
      });
      $[10] = alerts.length;
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    if ($[13] !== onClose) {
      t5 = open => !open && onClose();
      $[13] = onClose;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t1 = "flex flex-col gap-4";
    let t6;
    if ($[15] !== onSelect || $[16] !== t) {
      t6 = t7 => {
        const [assigneeName, groupAlerts] = t7;
        return <div key={assigneeName || "unassigned"} className="flex flex-col gap-2"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{assigneeName || t("applications.unassigned")}<span className="ml-2 font-normal normal-case tracking-normal">{groupAlerts.length}</span></p>{groupAlerts.map(alert_0 => <button key={alert_0.id} type="button" onClick={() => onSelect(alert_0.applicationId)} className="flex items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors hover:bg-muted/50">{alert_0.kind === "unassigned" ? <UserX className={cn("size-4 shrink-0", alert_0.severity === "error" ? "text-destructive" : "text-amber-600")} /> : <AlertTriangle className={cn("size-4 shrink-0", alert_0.severity === "error" ? "text-destructive" : "text-amber-600")} />}<span className="min-w-0 flex-1 truncate font-medium">{alert_0.shopName}</span><span className="shrink-0 text-xs text-muted-foreground">{alert_0.kind === "unassigned" ? t("applications.alert.unassigned") : t("applications.alert.stale", {
                count: alert_0.days
              })}</span></button>)}</div>;
      };
      $[15] = onSelect;
      $[16] = t;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t2 = ordered.map(t6);
    $[0] = alerts;
    $[1] = onClose;
    $[2] = onSelect;
    $[3] = t;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[18] !== t1 || $[19] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== T0 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t7 = <T0 open={t3} title={t4} onOpenChange={t5}>{t6}</T0>;
    $[21] = T0;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function _temp19(t0, t1) {
  const [a] = t0;
  const [b] = t1;
  return a === "" ? -1 : b === "" ? 1 : a.localeCompare(b);
}